import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Table, Tag, Tooltip, Badge, Descriptions, Empty, Spin, Card } from 'antd';
import Icons from '@/components/third-party/Icons';
import { get_batch_process_operations } from '@/utils/api';
import { resolveOperationExecutionStatus } from '@/utils/operationExecutionStatus';
import { fmt, yesNo, statusTagColor, personName, formatSeconds } from './helpers';
import OperationDetailPanel from './OperationDetailPanel';

// ==============================|| PROCESS OPERATIONS MODAL ||============================== //

const ProcessOperationsModal = ({ open, onClose, batchNumber, process }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);

  useEffect(() => {
    if (!open || !process?.process_id) return;
    setLoading(true);
    setData(null);
    get_batch_process_operations(
      { batch_number: batchNumber, process_id: process.process_id },
      (res) => {
        setData(res?.data || null);
        setLoading(false);
      }
    );
  }, [open, process, batchNumber]);

  const operations = data?.operations || [];
  const processInfo = data?.process || process || {};

  const columns = [
    { title: '#', dataIndex: 'operation_number', key: 'operation_number', width: 50, align: 'center' },
    { title: 'Code', dataIndex: 'operation_code', key: 'operation_code', width: 100, render: (v) => v || '—' },
    {
      title: 'Operation',
      dataIndex: 'operation_name',
      key: 'operation_name',
      width: 180,
      render: (v) => (
        <Tooltip title={v}>
          <div className="truncate" style={{ maxWidth: 170 }}>{v || '—'}</div>
        </Tooltip>
      ),
    },
    { title: 'Type', dataIndex: 'operation_type', key: 'operation_type', width: 120, render: (v) => <Tag>{v || '—'}</Tag> },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, r) => {
        const resolved = resolveOperationExecutionStatus(r.execution?.status);
        return <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>;
      },
    },
    { title: 'Machine', key: 'machine', width: 110, render: (_, r) => r.execution?.machine || '—' },
    { title: 'Scheduled', key: 'scheduled_time', width: 150, render: (_, r) => fmt(r.execution?.scheduled_time) },
    { title: 'Start', key: 'start', width: 150, render: (_, r) => fmt(r.execution?.start_time) },
    { title: 'End', key: 'end', width: 150, render: (_, r) => fmt(r.execution?.end_time) },
    {
      title: 'Duration',
      key: 'duration',
      width: 130,
      render: (_, r) => (
        <Tooltip title={`Paused: ${formatSeconds(r.execution?.paused_seconds)}`}>
          {formatSeconds(r.execution?.actual_duration_seconds)}
        </Tooltip>
      ),
    },
    {
      title: 'Started / Ended By',
      key: 'employees',
      width: 150,
      render: (_, r) => {
        const start = r.execution?.start_employee;
        const end = r.execution?.end_employee;
        const sameEmployee = start && end && start.id === end.id;
        return (
          <div style={{ fontSize: 12 }}>
            <div>{personName(start)}</div>
            {!sameEmployee && end && <div style={{ color: '#64748b' }}>Ended: {personName(end)}</div>}
          </div>
        );
      },
    },
    {
      title: 'Pass/Fail',
      dataIndex: 'is_pass_fail',
      key: 'is_pass_fail',
      width: 90,
      align: 'center',
      render: yesNo,
    },
    { title: 'E-Sign', dataIndex: 'is_esign_required', key: 'is_esign_required', width: 80, align: 'center', render: yesNo },
    {
      title: 'Supervisor Approval',
      key: 'supervisor_approval',
      width: 150,
      render: (_, r) => {
        if (r.supervisor_approval) {
          return <Tag color={statusTagColor(r.supervisor_approval.status)}>{r.supervisor_approval.status}</Tag>;
        }
        return r.is_supervisor_approval_required ? <Tag color="gold">Pending</Tag> : '—';
      },
    },
    {
      title: 'MBMR Approvals',
      key: 'recipe_approvals',
      width: 150,
      render: (_, r) =>
        (r.recipe_approvals || []).length === 0
          ? '—'
          : (r.recipe_approvals || []).map((a, i) => (
              <Tag key={i} color={statusTagColor(a.status)} style={{ marginBottom: 2 }}>
                {a.role}: {a.status}
              </Tag>
            )),
    },
    {
      title: 'Deviations',
      key: 'deviations',
      width: 110,
      align: 'center',
      render: (_, r) => {
        const b = r.deviations?.breakdown;
        const breakdownText = b
          ? `Parameter: ${b.parameter ?? 0} · Ingredient: ${b.ingredient ?? 0} · Sampling: ${b.sampling ?? 0} · Duration: ${b.duration ?? 0}${r.deviations?.has_ticket ? ' · Ticket raised' : ''}`
          : 'No deviations';
        return (
          <Tooltip title={breakdownText}>
            <Badge
              count={r.deviations?.total || 0}
              showZero
              color={r.deviations?.total ? '#dc2626' : '#94a3b8'}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Pauses/Holds',
      key: 'pauses_holds',
      width: 100,
      align: 'center',
      render: (_, r) => <Badge count={(r.pauses_holds || []).length} showZero color="#d97706" />,
    },
    {
      title: 'Details',
      key: 'details',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Details">
          <span className="action-icon flex items-center justify-center w-full">
            <Icons.Eye
              className="w-5 h-5 cursor-pointer text-blue-600 hover:text-blue-800"
              onClick={() => setSelectedOperation(record)}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Icons.Workflow size={18} className="text-blue-600" />
            <span>{processInfo.process_name || 'Process Operations'}</span>
            {processInfo.process_code && <Tag>{processInfo.process_code}</Tag>}
          </div>
        }
        open={open && !selectedOperation}
        onCancel={onClose}
        footer={null}
        width="90%"
        style={{ maxWidth: 1400 }}
        zIndex={1050}
        centered
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" tip="Loading operations…" />
          </div>
        ) : (
          <>
            <Card size="small" className="mb-3">
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="Duration">{processInfo.duration ?? '—'} min</Descriptions.Item>
                <Descriptions.Item label="Batch Number">{data?.batch_number || batchNumber}</Descriptions.Item>
                <Descriptions.Item label="Scheduled Start">{fmt(processInfo.schedule?.start_dt)}</Descriptions.Item>
                <Descriptions.Item label="Scheduled End">{fmt(processInfo.schedule?.end_dt)}</Descriptions.Item>
              </Descriptions>
            </Card>
            <Table
              size="small"
              bordered
              dataSource={operations.map((op, i) => ({ ...op, key: op.operation_id ?? i }))}
              columns={columns}
              pagination={false}
              // Responsive: was 480px flat, anchored to a 900px reference viewport so it scales on larger/smaller screens
              scroll={{ x: 'max-content', y: 'calc(100vh - 420px)' }}
              locale={{ emptyText: <Empty description="No operations found for this process" /> }}
            />
          </>
        )}
      </Modal>

      <Modal
        title={
          <div className="flex items-center gap-2">
            <Icons.ClipboardList size={18} className="text-blue-600" />
            <span>{selectedOperation?.operation_name || 'Operation Details'}</span>
            {selectedOperation?.operation_code && <Tag>{selectedOperation.operation_code}</Tag>}
          </div>
        }
        open={!!selectedOperation}
        onCancel={() => setSelectedOperation(null)}
        footer={null}
        width="85%"
        style={{ maxWidth: 1200 }}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
        zIndex={1100}
        centered
        destroyOnClose
      >
        <div className="space-y-3">
          {(selectedOperation?.pauses_holds || []).length > 0 && (
            <Table
              size="small"
              title={() => 'Pauses / Holds'}
              dataSource={(selectedOperation.pauses_holds || []).map((p, i) => ({ ...p, key: i }))}
              pagination={false}
              columns={[
                { title: 'Action', dataIndex: 'action', key: 'action', render: (v) => <Tag color="orange">{v || '—'}</Tag> },
                { title: 'Started At', dataIndex: 'started_at', key: 'started_at', render: fmt },
                { title: 'Ended At', dataIndex: 'ended_at', key: 'ended_at', render: fmt },
                { title: 'Duration', dataIndex: 'duration_seconds', key: 'duration_seconds', render: (v) => formatSeconds(v) },
                { title: 'Reason', dataIndex: 'reason', key: 'reason', render: (v) => v || '—' },
                { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (v) => v || '—' },
                { title: 'Started By', dataIndex: 'started_by', key: 'started_by', render: personName },
              ]}
            />
          )}
          {selectedOperation && (
            <OperationDetailPanel batchNumber={batchNumber} operationId={selectedOperation.operation_id} />
          )}
        </div>
      </Modal>
    </>
  );
};

ProcessOperationsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  batchNumber: PropTypes.string,
  process: PropTypes.object,
};

export default ProcessOperationsModal;
